/**
 * Équilibrage (`GET|PATCH /admin/balance`).
 *
 * L'écran expose **toutes** les clés de `balance.json`, y compris les six paramètres de géométrie
 * des archétypes de tours (A-29) : ce sont des réglages de jeu, pas des constantes de code, et
 * c'est précisément ce que le back-office doit rendre modifiable sans redéploiement.
 *
 * Les bornes affichées sous chaque champ sont celles du schéma (`balanceSchema`) : elles ne sont
 * pas une politesse, elles évitent un aller-retour réseau pour une valeur évidemment refusée.
 */
import { balanceSchema } from '@elemora/shared/content';
import { useEffect, useState } from 'react';

import { lireBalance, modifierBalance } from '../api/admin';
import { ErreurApi, erreursDeValidation, versErreurApi } from '../api/erreurs';
import { M } from '../catalogue/messages';
import { useChargement } from '../hooks/use-chargement';
import { Bandeau, BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Champ } from '../ui/Champ';
import { Chargement } from '../ui/Chargement';
import { Coquille } from '../ui/Coquille';
import { Panneau } from '../ui/Panneau';

import type { AdminBalancePatch } from '@elemora/shared/api';
import type { Balance, Rarete } from '@elemora/shared/content';

/** Description d'un champ numérique : clé du contrat, libellé, bornes rappelées. */
interface ChampBalance {
  readonly cle: CleNumerique;
  readonly libelle: string;
  readonly bornes: string;
  readonly entier: boolean;
}

/** Clé de `balance` éditable : nom de champ du contrat. */
type CleNumerique = string;

/**
 * Lit une valeur numérique de `balance` par son nom.
 *
 * Pourquoi une indexation par chaîne plutôt que `keyof Balance` : les six clés de géométrie des
 * archétypes (A-29) sont arrivées dans le **contrat** avec la carte M3-07. Le back-office, lui,
 * itère une **liste de noms de champs** : il n'a pas besoin que le type les connaisse pour les
 * afficher ni les envoyer. Cette indirection garde donc l'outil compilable aussi bien avant
 * qu'après l'ajout de ces clés, et une clé absente vaut `0` au lieu de casser l'écran.
 */
function lireNombre(balance: Balance, cle: string): number {
  const valeur = (balance as unknown as Record<string, unknown>)[cle];
  return typeof valeur === 'number' ? valeur : 0;
}

const CHAMPS: readonly ChampBalance[] = [
  {
    cle: 'pv_parcours_defaut',
    libelle: 'PV du parcours par défaut',
    bornes: M.equilibrage.bornes.entierPositif,
    entier: true,
  },
  {
    cle: 'eclats_depart',
    libelle: 'Éclats de départ',
    bornes: M.equilibrage.bornes.entierNonNegatif,
    entier: true,
  },
  {
    cle: 'jokers_depart',
    libelle: 'Jokers de départ',
    bornes: M.equilibrage.bornes.entierNonNegatif,
    entier: true,
  },
  {
    cle: 'eclats_par_ennemi_defaut',
    libelle: 'Éclats par ennemi (valeur par défaut)',
    bornes: M.equilibrage.bornes.entierNonNegatif,
    entier: true,
  },
  {
    cle: 'bonus_fin_niveau',
    libelle: 'Bonus d’éclats en fin de niveau',
    bornes: M.equilibrage.bornes.entierNonNegatif,
    entier: true,
  },
  {
    cle: 'chance_carte_bonus',
    libelle: 'Chance de carte bonus (vague de boss)',
    bornes: M.equilibrage.bornes.probabilite,
    entier: false,
  },
  {
    cle: 'nb_vagues_limite',
    libelle: 'Vagues par niveau (mode limité)',
    bornes: M.equilibrage.bornes.entierPositif,
    entier: true,
  },
  {
    cle: 'nb_vagues_limite_debut',
    libelle: 'Vagues des premiers niveaux',
    bornes: M.equilibrage.bornes.entierPositif,
    entier: true,
  },
  {
    cle: 'niveau_premier_boss',
    libelle: 'Numéro du premier niveau à boss',
    bornes: M.equilibrage.bornes.entierPositif,
    entier: true,
  },
  {
    cle: 'nb_vagues_illimite',
    libelle: 'Vagues par round (mode illimité)',
    bornes: M.equilibrage.bornes.entierPositif,
    entier: true,
  },
  {
    cle: 'pause_illimite_ms',
    libelle: 'Pause entre deux rounds (ms)',
    bornes: M.equilibrage.bornes.entierNonNegatif,
    entier: true,
  },
  {
    cle: 'carte_choix_par_vague',
    libelle: 'Cartes proposées par vague',
    bornes: M.equilibrage.bornes.entierPositif,
    entier: true,
  },
  {
    cle: 'cartes_verrouillees_par_tirage',
    libelle: 'Cartes verrouillées par tirage',
    bornes: M.equilibrage.bornes.entierNonNegatif,
    entier: true,
  },
  {
    cle: 'multiplicateur_difficulte_illimite',
    libelle: 'Multiplicateur de difficulté (illimité)',
    bornes: M.equilibrage.bornes.superieurAUn,
    entier: false,
  },
];

/** Les six paramètres de géométrie des archétypes (A-29), présentés à part. */
const CHAMPS_ARCHETYPES: readonly ChampBalance[] = [
  {
    cle: 'zone_rayon_px',
    libelle: 'Rayon de la zone d’effet (px)',
    bornes: M.equilibrage.bornes.positif,
    entier: false,
  },
  {
    cle: 'zone_facteur_secondaire',
    libelle: 'Facteur des cibles secondaires (zone)',
    bornes: M.equilibrage.bornes.facteurUnMax,
    entier: false,
  },
  {
    cle: 'ligne_facteur_chute',
    libelle: 'Chute de dégâts par cible (ligne)',
    bornes: M.equilibrage.bornes.facteurUnMax,
    entier: false,
  },
  {
    cle: 'chain_cibles_max',
    libelle: 'Cibles totales d’une chaîne',
    bornes: M.equilibrage.bornes.entierPositif,
    entier: true,
  },
  {
    cle: 'chain_portee_saut_px',
    libelle: 'Portée d’un saut de chaîne (px)',
    bornes: M.equilibrage.bornes.positif,
    entier: false,
  },
  {
    cle: 'chain_facteur_chute',
    libelle: 'Chute de dégâts par saut (chaîne)',
    bornes: M.equilibrage.bornes.facteurUnMax,
    entier: false,
  },
];

/** Toutes les clés scalaires, dans l'ordre d'affichage. */
const TOUS_LES_CHAMPS: readonly ChampBalance[] = [...CHAMPS, ...CHAMPS_ARCHETYPES];

const RARETES: readonly Rarete[] = ['commune', 'rare', 'legendaire', 'mythique'];

/** Valeurs de saisie : tout est chaîne tant que l'administrateur tape. */
interface Saisie {
  readonly scalaires: Readonly<Record<string, string>>;
  readonly rarete: Readonly<Record<string, string>>;
  readonly deckDepart: string;
}

function saisieDe(balance: Balance): Saisie {
  const scalaires: Record<string, string> = {};
  for (const champ of TOUS_LES_CHAMPS)
    scalaires[champ.cle] = String(lireNombre(balance, champ.cle));
  const rarete: Record<string, string> = {};
  for (const cle of RARETES) rarete[cle] = String(balance.points_rarete[cle]);
  return { scalaires, rarete, deckDepart: balance.deck_depart.join(', ') };
}

/** Convertit une saisie en nombre ; `NaN` devient `0` pour rester sérialisable. */
function nombre(valeur: string): number {
  const converti = Number(valeur);
  return Number.isFinite(converti) ? converti : 0;
}

/** Ne conserve que les clés effectivement modifiées (patch partiel strict). */
function construirePatch(initial: Balance, saisie: Saisie): AdminBalancePatch {
  const patch: Record<string, unknown> = {};
  for (const champ of TOUS_LES_CHAMPS) {
    const valeur = nombre(saisie.scalaires[champ.cle] ?? '0');
    if (valeur !== lireNombre(initial, champ.cle)) patch[champ.cle] = valeur;
  }
  const rarete: Record<string, number> = {};
  let rareteModifiee = false;
  for (const cle of RARETES) {
    const valeur = Math.trunc(nombre(saisie.rarete[cle] ?? '0'));
    rarete[cle] = valeur;
    if (valeur !== initial.points_rarete[cle]) rareteModifiee = true;
  }
  if (rareteModifiee) patch['points_rarete'] = { ...initial.points_rarete, ...rarete };
  const deck = saisie.deckDepart
    .split(/[,\n]/)
    .map((identifiant) => identifiant.trim())
    .filter((identifiant) => identifiant.length > 0);
  if (JSON.stringify(deck) !== JSON.stringify(initial.deck_depart)) patch['deck_depart'] = deck;
  return patch as AdminBalancePatch;
}

export function EcranEquilibrage() {
  const chargement = useChargement((signal) => lireBalance(signal));
  const [saisie, setSaisie] = useState<Saisie | null>(null);
  const [reference, setReference] = useState<Balance | null>(null);
  const [refus, setRefus] = useState<ErreurApi | null>(null);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (chargement.donnees === null) return;
    setReference(chargement.donnees.balance);
    setSaisie(saisieDe(chargement.donnees.balance));
    setErreurs({});
    setRefus(null);
  }, [chargement.donnees]);

  async function enregistrer(): Promise<void> {
    if (enCours || reference === null || saisie === null) return;
    setSucces(null);
    setRefus(null);
    setErreurs({});

    const patch = construirePatch(reference, saisie);
    if (Object.keys(patch).length === 0) {
      setSucces(M.cartes.aucuneModification);
      return;
    }

    // Validation locale sur l'**objet fusionné** : c'est la même règle que le serveur, qui
    // revalide l'entité entière après fusion et refuse un état globalement incohérent.
    const candidat = { ...reference, ...patch } as Balance;
    const verification = balanceSchema.safeParse(candidat);
    if (!verification.success) {
      const parChemin: Record<string, string> = {};
      for (const probleme of verification.error.issues) {
        parChemin[probleme.path.join('.')] = probleme.message;
      }
      setErreurs(parChemin);
      setRefus(
        new ErreurApi({
          code: 'VALIDATION_FAILED',
          message: M.cartes.validationLocale,
          statutHttp: 0,
          messageServeur: M.cartes.validationLocale,
        }),
      );
      return;
    }

    setEnCours(true);
    try {
      const resultat = await modifierBalance(patch);
      setReference(resultat.balance);
      setSaisie(saisieDe(resultat.balance));
      setSucces(M.equilibrage.enregistre);
    } catch (inconnu) {
      const erreurApi = versErreurApi(inconnu);
      setRefus(erreurApi);
      const parChemin: Record<string, string> = {};
      for (const defaut of erreursDeValidation(erreurApi)) {
        parChemin[defaut.chemin.replace(/^(body|query)\./, '')] = defaut.message;
      }
      setErreurs(parChemin);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Coquille
      titre={M.equilibrage.titre}
      sousTitre={M.equilibrage.sousTitre}
      actions={
        <Bouton variante="secondaire" onClick={chargement.recharger}>
          {M.commun.actualiser}
        </Bouton>
      }
    >
      <Panneau aide={M.avertissements.brouillon}>
        {chargement.erreur === null ? null : (
          <BandeauErreur erreur={chargement.erreur} surReessai={chargement.recharger} />
        )}
        {chargement.chargement ? <Chargement /> : null}

        {saisie === null || reference === null ? null : (
          <form
            className="formulaire"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              void enregistrer();
            }}
          >
            {refus === null ? null : <BandeauErreur erreur={refus} />}
            {succes === null ? null : <Bandeau texte={succes} ton="succes" />}

            <div className="formulaire__grille formulaire__grille--trois">
              {CHAMPS.map((champ) => (
                <Champ
                  key={champ.cle}
                  id={`balance-${champ.cle}`}
                  etiquette={champ.libelle}
                  type="number"
                  step={champ.entier ? '1' : '0.01'}
                  value={saisie.scalaires[champ.cle] ?? ''}
                  aide={`${champ.cle} — ${champ.bornes}`}
                  erreur={erreurs[champ.cle] ?? null}
                  onChange={(evenement) =>
                    setSaisie({
                      ...saisie,
                      scalaires: { ...saisie.scalaires, [champ.cle]: evenement.target.value },
                    })
                  }
                />
              ))}
            </div>

            <fieldset className="formulaire__groupe">
              <legend>{M.equilibrage.archetypesTitre}</legend>
              <p className="panneau__aide">{M.equilibrage.archetypesAide}</p>
              <div className="formulaire__grille formulaire__grille--trois">
                {CHAMPS_ARCHETYPES.map((champ) => (
                  <Champ
                    key={champ.cle}
                    id={`balance-${champ.cle}`}
                    etiquette={champ.libelle}
                    type="number"
                    step={champ.entier ? '1' : '0.01'}
                    value={saisie.scalaires[champ.cle] ?? ''}
                    aide={`${champ.cle} — ${champ.bornes}`}
                    erreur={erreurs[champ.cle] ?? null}
                    onChange={(evenement) =>
                      setSaisie({
                        ...saisie,
                        scalaires: { ...saisie.scalaires, [champ.cle]: evenement.target.value },
                      })
                    }
                  />
                ))}
              </div>
            </fieldset>

            <fieldset className="formulaire__groupe">
              <legend>{M.equilibrage.pointsRarete}</legend>
              <div className="formulaire__grille">
                {RARETES.map((cle) => (
                  <Champ
                    key={cle}
                    id={`balance-rarete-${cle}`}
                    etiquette={M.raretes[cle]}
                    type="number"
                    min="0"
                    step="1"
                    value={saisie.rarete[cle] ?? ''}
                    aide={`points_rarete.${cle} — ${M.equilibrage.bornes.entierNonNegatif}`}
                    erreur={erreurs[`points_rarete.${cle}`] ?? null}
                    onChange={(evenement) =>
                      setSaisie({
                        ...saisie,
                        rarete: { ...saisie.rarete, [cle]: evenement.target.value },
                      })
                    }
                  />
                ))}
              </div>
            </fieldset>

            <Champ
              id="balance-deck-depart"
              etiquette={M.equilibrage.deckDepart}
              value={saisie.deckDepart}
              aide={`deck_depart — ${M.equilibrage.bornes.listeIdentifiants} · ${M.equilibrage.deckDepartAide}`}
              erreur={erreurs['deck_depart'] ?? null}
              onChange={(evenement) => setSaisie({ ...saisie, deckDepart: evenement.target.value })}
            />

            <div className="formulaire__actions">
              <Bouton type="submit" enCours={enCours} texteEnCours={M.commun.enregistrementEnCours}>
                {M.commun.enregistrer}
              </Bouton>
              <button
                type="button"
                className="lien-action"
                onClick={() => setSaisie(saisieDe(reference))}
              >
                {M.commun.annuler}
              </button>
            </div>
          </form>
        )}
      </Panneau>
    </Coquille>
  );
}
